import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { type MouseEvent, useEffect, useState } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { fontFamilies, r, s } from '@/theme/tokens';
import { Stack, Text } from '@/ui';
import { Box, Button, IconButton, Menu, MenuItem, TextField } from '@/ui/primitives';
import type { TagColor } from '@/shared/data/tags';
const tagColorOptions: Array<{ value: TagColor; swatch: string }> = [
  { value: 'neutral', swatch: '#8D949E' },
  { value: 'green', swatch: '#43A047' },
  { value: 'orange', swatch: '#FB8C00' },
  { value: 'red', swatch: '#EF5350' },
  { value: 'blue', swatch: '#42A5F5' },
  { value: 'purple', swatch: '#AB47BC' },
  { value: 'teal', swatch: '#00ACC1' },
  { value: 'brown', swatch: '#8D6E63' },
];
const defaultTagColor: TagColor = 'neutral';
const tagColorSwatchSize = '18px';
const tagColorSwatchRadius = '6px';
const tagColorControlSize = '32px';
const tagColorSelectWidth = tagColorControlSize;
const tagColorSwatchSx = {
  display: 'inline-flex',
  width: tagColorSwatchSize,
  height: tagColorSwatchSize,
  minWidth: tagColorSwatchSize,
  minHeight: tagColorSwatchSize,
  maxWidth: tagColorSwatchSize,
  maxHeight: tagColorSwatchSize,
  flex: '0 0 auto',
  flexShrink: 0,
  aspectRatio: '1 / 1',
  boxSizing: 'border-box',
  borderRadius: tagColorSwatchRadius,
  border: '1px solid',
  borderColor: 'divider',
} as const;
const testNameCodeSx = {
  display: 'inline',
  fontFamily: fontFamilies.mono,
  backgroundColor: 'action.hover',
  borderRadius: s('xxs'),
  pl: s('sm'),
  pr: s('xs'),
  whiteSpace: 'normal',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
  boxDecorationBreak: 'clone',
} as const;
type TableActionsCellProps = {
  rowName: string;
  editTagsEntityNoun?: 'test' | 'suite';
  rowTags?: string[];
  rowTagColors?: TagColor[];
  showEditTagsAction?: boolean;
  onUpdateTagsColor?: (input: { tags: string[]; colors: TagColor[] }) => void;
  updateTagsColorLoading?: boolean;
  onDelete?: () => void;
  disableDelete?: boolean;
};

export const TableActionsCell = ({
  rowName,
  editTagsEntityNoun = 'test',
  rowTags = [],
  rowTagColors = [],
  showEditTagsAction = true,
  onUpdateTagsColor,
  updateTagsColorLoading = false,
  onDelete,
  disableDelete = false,
}: TableActionsCellProps): JSX.Element => {
  const resolvedRowName = rowName.trim() || 'this test';
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [colorMenuState, setColorMenuState] = useState<{
    index: number;
    anchorEl: HTMLElement;
  } | null>(null);
  const [editTagsOpen, setEditTagsOpen] = useState(false);
  const [draftTags, setDraftTags] = useState<string[]>(rowTags.length > 0 ? rowTags : []);
  const [draftTagColors, setDraftTagColors] = useState<TagColor[]>(
    rowTags.length > 0 ? rowTags.map((_, index) => rowTagColors[index] ?? defaultTagColor) : [],
  );
  const menuOpen = Boolean(anchorEl);

  useEffect(() => {
    if (!editTagsOpen) return;
    setDraftTags(rowTags.length > 0 ? rowTags : []);
    setDraftTagColors(
      rowTags.length > 0 ? rowTags.map((_, index) => rowTagColors[index] ?? defaultTagColor) : [],
    );
  }, [editTagsOpen, rowTagColors, rowTags]);

  const handleOpen = (event: MouseEvent<HTMLElement>): void => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const handleOpenColorMenu =
    (index: number) =>
    (event: MouseEvent<HTMLElement>): void => {
      event.stopPropagation();
      setColorMenuState({ index, anchorEl: event.currentTarget });
    };

  const handleCloseColorMenu = (): void => {
    setColorMenuState(null);
  };

  const normalizeTagsAndColors = (
    tags: string[],
    colors: TagColor[],
  ): { tags: string[]; colors: TagColor[] } => {
    const normalizedTags = tags.map((tag) => tag.trim()).filter((tag) => tag.length > 0);
    const normalizedColors =
      normalizedTags.length === colors.length
        ? colors
        : normalizedTags.map((_tag, index) => colors[index] ?? defaultTagColor);
    return { tags: normalizedTags, colors: normalizedColors };
  };

  const normalizedInitialTagsAndColors = normalizeTagsAndColors(
    rowTags.length > 0 ? rowTags : [],
    rowTags.length > 0 ? rowTags.map((_, index) => rowTagColors[index] ?? defaultTagColor) : [],
  );
  const normalizedDraftTagsAndColors = normalizeTagsAndColors(draftTags, draftTagColors);
  const hasTagChanges =
    normalizedDraftTagsAndColors.tags.length !== normalizedInitialTagsAndColors.tags.length ||
    normalizedDraftTagsAndColors.colors.length !== normalizedInitialTagsAndColors.colors.length ||
    normalizedDraftTagsAndColors.tags.some(
      (tag, index) => tag !== normalizedInitialTagsAndColors.tags[index],
    ) ||
    normalizedDraftTagsAndColors.colors.some(
      (color, index) => color !== normalizedInitialTagsAndColors.colors[index],
    );
  const cascadeCopy =
    editTagsEntityNoun === 'suite'
      ? 'Tags will be applied to all associated suite executions, tests and test executions.'
      : 'Tags will be applied to all associated test executions.';

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        pt: '8px',
      }}
    >
      <IconButton
        aria-label={`Row actions for ${rowName}`}
        aria-haspopup="menu"
        aria-expanded={menuOpen ? 'true' : undefined}
        size="medium"
        onClick={handleOpen}
        sx={{
          width: '28px',
          height: '28px',
          minWidth: '28px',
          minHeight: '28px',
          p: '0',
          borderRadius: r('pill'),
          '&:hover, &.Mui-focusVisible': {
            backgroundColor: 'rgba(255, 255, 255, 0.22)',
          },
        }}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: s('sm'),
              border: (theme) => `1px solid ${theme.app.colors.border}`,
              borderRadius: `${r('card')} !important`,
              backgroundColor: 'background.paper',
              backgroundImage: 'none',
              boxShadow: 'none',
              '--Paper-overlay': 'none',
            },
          },
          list: {
            sx: {
              py: s('none'),
            },
          },
        }}
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        {showEditTagsAction ? (
          <MenuItem
            disabled={updateTagsColorLoading}
            sx={{ minHeight: '32px', gap: s('sm'), px: s('md'), py: s('sm') }}
            onClick={(event) => {
              event.stopPropagation();
              handleClose();
              setEditTagsOpen(true);
            }}
          >
            <LocalOfferIcon sx={{ fontSize: (theme) => theme.app.iconSizes.sm }} />
            <Text role="body">Edit tags</Text>
          </MenuItem>
        ) : null}
        <MenuItem
          disabled={disableDelete}
          sx={{ minHeight: '32px', gap: s('sm'), px: s('md'), py: s('sm') }}
          onClick={(event) => {
            event.stopPropagation();
            handleClose();
            onDelete?.();
          }}
        >
          <DeleteIcon
            htmlColor="currentColor"
            sx={{ fontSize: (theme) => theme.app.iconSizes.sm, color: 'error.main' }}
          />
          <Text role="body">Delete</Text>
        </MenuItem>
      </Menu>
      <ConfirmDialog
        open={editTagsOpen}
        title="Edit Tags"
        description={
          <Stack gap="md">
            <Text role="body" color="textSecondary">
              Updating tags for {editTagsEntityNoun}{' '}
              <Box component="code" sx={testNameCodeSx}>
                {resolvedRowName}
              </Box>
              .
            </Text>
            <Text role="body" color="textSecondary">
              {cascadeCopy}
            </Text>
          </Stack>
        }
        confirmLabel="Update"
        confirmColor="primary"
        confirmDisabled={!hasTagChanges}
        footerGap="xs"
        loading={updateTagsColorLoading}
        onClose={() => {
          if (updateTagsColorLoading) return;
          setEditTagsOpen(false);
        }}
        onConfirm={() => {
          onUpdateTagsColor?.(normalizedDraftTagsAndColors);
          setEditTagsOpen(false);
        }}
      >
        <Stack gap="none" sx={{ width: '100%', mt: s('none') }}>
          <Text role="label" color="textSecondary">
            Tags
          </Text>
          <Stack gap="md" sx={{ mt: s('none') }}>
            {draftTags.map((tag, index) => (
              <Box
                key={`${rowName}-tag-${index}`}
                sx={{ display: 'flex', alignItems: 'center', gap: s('sm'), width: '100%' }}
              >
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    display: 'grid',
                    gridTemplateColumns: `minmax(0, 1fr) ${tagColorSelectWidth}`,
                    alignItems: 'center',
                    gap: s('md'),
                  }}
                >
                  <TextField
                    size="small"
                    inputHeight={32}
                    value={tag}
                    onChange={(event) => {
                      const nextValue = event.target.value;
                      setDraftTags((prev) =>
                        prev.map((item, itemIndex) => (itemIndex === index ? nextValue : item)),
                      );
                    }}
                    fullWidth
                    sx={{
                      '& .MuiInputBase-root, & .MuiOutlinedInput-root': {
                        backgroundColor: 'background.default',
                        minHeight: tagColorControlSize,
                        height: tagColorControlSize,
                      },
                    }}
                  />
                  <Box
                    role="button"
                    tabIndex={0}
                    aria-label={`Choose color for tag ${index + 1}`}
                    onClick={handleOpenColorMenu(index)}
                    onKeyDown={(event) => {
                      if (event.key !== 'Enter' && event.key !== ' ') return;
                      event.preventDefault();
                      event.stopPropagation();
                      setColorMenuState({ index, anchorEl: event.currentTarget as HTMLElement });
                    }}
                    sx={{
                      minWidth: tagColorSelectWidth,
                      width: tagColorSelectWidth,
                      maxWidth: tagColorSelectWidth,
                      minHeight: tagColorControlSize,
                      height: tagColorControlSize,
                      maxHeight: tagColorControlSize,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: r('md'),
                      backgroundColor: 'background.default',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                    }}
                  >
                    <Box
                      component="span"
                      sx={[
                        tagColorSwatchSx,
                        {
                          backgroundColor:
                            tagColorOptions.find(
                              (option) =>
                                option.value === (draftTagColors[index] ?? defaultTagColor),
                            )?.swatch ?? tagColorOptions[0].swatch,
                        },
                      ]}
                    />
                  </Box>
                </Box>
                <Menu
                  anchorEl={colorMenuState?.anchorEl ?? null}
                  open={colorMenuState?.index === index}
                  onClose={handleCloseColorMenu}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  slotProps={{
                    paper: {
                      sx: {
                        minWidth: tagColorSelectWidth,
                        width: tagColorSelectWidth,
                        maxWidth: tagColorSelectWidth,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: `${r('md')} !important`,
                        backgroundColor: 'background.default',
                        backgroundImage: 'none',
                        boxShadow: 'none',
                        overflow: 'hidden',
                      },
                    },
                    list: {
                      sx: {
                        p: s('none'),
                        m: s('none'),
                        backgroundColor: 'background.default',
                      },
                    },
                  }}
                >
                  {tagColorOptions.map((option) => (
                    <MenuItem
                      key={option.value}
                      onClick={() => {
                        setDraftTagColors((prev) =>
                          prev.map((item, itemIndex) =>
                            itemIndex === index ? option.value : item,
                          ),
                        );
                        handleCloseColorMenu();
                      }}
                      sx={{
                        minHeight: `calc(${s('2xl')} + ${s('xs')})`,
                        px: s('md'),
                        py: s('md'),
                        justifyContent: 'center',
                        borderRadius: r('none'),
                        backgroundColor:
                          (draftTagColors[index] ?? defaultTagColor) === option.value
                            ? 'action.selected'
                            : undefined,
                      }}
                    >
                      <Box
                        component="span"
                        sx={[tagColorSwatchSx, { backgroundColor: option.swatch }]}
                      />
                    </MenuItem>
                  ))}
                </Menu>
                <IconButton
                  aria-label={`Remove tag ${index + 1}`}
                  size="medium"
                  sx={{
                    width: `calc(${s('2xl')} + ${s('sm')})`,
                    height: `calc(${s('2xl')} + ${s('sm')})`,
                    minWidth: `calc(${s('2xl')} + ${s('sm')})`,
                    minHeight: `calc(${s('2xl')} + ${s('sm')})`,
                    p: s('none'),
                    borderRadius: r('pill'),
                    '&:hover, &.Mui-focusVisible': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                  onClick={() => {
                    setDraftTags((prev) => prev.filter((_item, itemIndex) => itemIndex !== index));
                    setDraftTagColors((prev) =>
                      prev.filter((_item, itemIndex) => itemIndex !== index),
                    );
                  }}
                >
                  <DeleteIcon
                    sx={{ color: 'error.main', fontSize: `calc(${s('2xl')} + ${s('xs')})` }}
                  />
                </IconButton>
              </Box>
            ))}
          </Stack>
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: s('sm') }}>
            <Button
              variant="text"
              startIcon={<AddIcon sx={{ fontSize: (theme) => theme.app.iconSizes.sm }} />}
              onClick={() => {
                setDraftTags((prev) => [...prev, '']);
                setDraftTagColors((prev) => [...prev, defaultTagColor]);
              }}
              sx={{
                pl: s('none'),
                minWidth: 0,
                '& .MuiButton-startIcon': {
                  marginLeft: s('none'),
                  marginRight: s('xxxs'),
                },
              }}
            >
              Add New
            </Button>
          </Box>
        </Stack>
      </ConfirmDialog>
    </Box>
  );
};

export { TableCellContainer } from './testsTableSharedCells';
